/**
 * Gamification API Client
 *
 * All calls are proxied through Next.js API routes (/api/gamification/*)
 * which handle JWT authentication and token refresh automatically.
 */

export interface GamificationProfile {
    id: string
    tenantId: string
    redmineUserId: number
    username: string
    email?: string
    level: number
    levelName: string
    currentXP: number
    totalXP: number
    monthlyXP: number
    weeklyXP: number
    lifetimeXP: number
    currentStreak: number
    longestStreak: number
    lastActivityDate?: string
    lastSyncDate?: string
    achievements: any[]
    statistics: any
    monthlyStatistics: any
    weeklyStatistics: any
    createdAt: string
    updatedAt: string
}

export interface ActivityLog {
    id: string
    tenantId: string
    redmineUserId: number
    username: string
    activityType: string
    description: string
    xpEarned: number
    timestamp: string
    metadata?: any
}

export interface LeaderboardEntry {
    redmineUserId: number
    username: string
    email?: string | null
    level: number
    levelName?: string
    xp: number
    streak: number
    badgeCount: number
    rank: number
    userId?: string // alias for compatibility
}

/**
 * Normalize backend profile response (lowercase xp fields) to frontend format (uppercase XP)
 */
function normalizeProfile(data: any): GamificationProfile {
    return {
        id: data.id,
        tenantId: data.tenantId || data.tenant_id,
        redmineUserId: data.redmineUserId || data.redmine_user_id,
        username: data.username,
        email: data.email,
        level: data.level || 1,
        levelName: data.levelName || data.level_name || 'New Dawn',
        currentXP: data.currentXp ?? data.currentXP ?? data.current_xp ?? 0,
        totalXP: data.totalXp ?? data.totalXP ?? data.total_xp ?? 0,
        monthlyXP: data.monthlyXp ?? data.monthlyXP ?? data.monthly_xp ?? 0,
        weeklyXP: data.weeklyXp ?? data.weeklyXP ?? data.weekly_xp ?? 0,
        lifetimeXP: data.lifetimeXp ?? data.lifetimeXP ?? data.lifetime_xp ?? 0,
        currentStreak: data.currentStreak ?? data.current_streak ?? 0,
        longestStreak: data.longestStreak ?? data.longest_streak ?? 0,
        lastActivityDate: data.lastActivityDate || data.last_activity_date,
        lastSyncDate: data.lastSyncDate || data.last_sync_date,
        achievements: (data.achievements || []).map((a: any) => ({
            ...a,
            achievementId: a.achievementId || a.achievementKey || a.achievement_key,
            achievementKey: a.achievementKey || a.achievementId || a.achievement_key,
        })),
        statistics: data.statistics || {},
        monthlyStatistics: data.monthlyStatistics || data.monthly_statistics || {},
        weeklyStatistics: data.weeklyStatistics || data.weekly_statistics || {},
        createdAt: data.createdAt || data.created_at,
        updatedAt: data.updatedAt || data.updated_at,
    }
}

/**
 * Normalize leaderboard entry
 */
function normalizeLeaderboardEntry(data: any): LeaderboardEntry {
    return {
        rank: data.rank || 0,
        redmineUserId: data.redmineUserId || data.redmine_user_id,
        username: data.username,
        email: data.email,
        level: data.level || 1,
        levelName: data.levelName || data.level_name,
        xp: data.xp ?? data.monthlyXp ?? data.monthlyXP ?? data.monthly_xp ?? 0,
        streak: data.streak ?? data.currentStreak ?? data.current_streak ?? 0,
        badgeCount: data.badgeCount ?? data.badge_count ?? 0,
        userId: String(data.redmineUserId || data.redmine_user_id),
    }
}

/**
 * Get gamification profile via proxy route (JWT authenticated)
 */
export async function getProfile(
    _tenantId: string,
    userId: number
): Promise<GamificationProfile | null> {
    const url = `/api/gamification/profile?userId=${userId}`
    console.log('[Gamification API] getProfile() → GET', url)

    try {
        const response = await fetch(url)
        console.log('[Gamification API] getProfile() response:', response.status, response.statusText)

        if (response.status === 404) {
            console.log('[Gamification API] getProfile() → 404, no profile found')
            return null
        }

        if (!response.ok) {
            const errorBody = await response.text()
            console.error('[Gamification API] getProfile() error body:', errorBody)
            throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText} - ${errorBody}`)
        }

        const data = await response.json()
        console.log('[Gamification API] getProfile() raw data:', data)
        if (!data) return null
        return normalizeProfile(data)
    } catch (err) {
        console.error('[Gamification API] getProfile() exception:', err)
        throw err
    }
}

/**
 * Create or get profile via proxy route (JWT authenticated)
 */
export async function createOrGetProfile(
    _tenantId: string,
    userId: number,
    username: string,
    email?: string
): Promise<GamificationProfile> {
    const payload = { userId, username, email }
    console.log('[Gamification API] createOrGetProfile() → POST /api/gamification/profile', payload)

    const response = await fetch('/api/gamification/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
    console.log('[Gamification API] createOrGetProfile() response:', response.status, response.statusText)

    if (!response.ok) {
        const errorBody = await response.text()
        console.error('[Gamification API] createOrGetProfile() error body:', errorBody)
        throw new Error(`Failed to create profile: ${response.status} ${response.statusText} - ${errorBody}`)
    }

    const data = await response.json()
    return normalizeProfile(data)
}

/**
 * Sync gamification data from Redmine database via proxy route (JWT authenticated)
 */
export async function syncGamificationData(
    userId: number
): Promise<{
    success: boolean
    profile: GamificationProfile
    activitiesProcessed: number
    xpEarned: number
}> {
    const payload = { userId }
    console.log('[Gamification API] syncGamificationData() → POST /api/gamification/sync', payload)

    try {
        const response = await fetch('/api/gamification/sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
        console.log('[Gamification API] syncGamificationData() response:', response.status, response.statusText)

        if (!response.ok) {
            const errorText = await response.text()
            console.error('[Gamification API] syncGamificationData() error response body:', errorText)
            let errorData: any = {}
            try { errorData = JSON.parse(errorText) } catch { /* not JSON */ }
            throw new Error(errorData?.error || errorData?.message || `Sync failed: ${response.status} ${response.statusText} - ${errorText}`)
        }

        const data = await response.json()
        console.log('[Gamification API] syncGamificationData() raw data:', data)
        return {
            ...data,
            profile: normalizeProfile(data.profile),
        }
    } catch (err) {
        console.error('[Gamification API] syncGamificationData() exception:', err)
        throw err
    }
}

/**
 * Award XP to user
 */
export async function awardXP(
    tenantId: string,
    userId: number,
    xpAmount: number,
    activityType: string,
    description: string,
    metadata?: Record<string, any>
): Promise<{
    leveledUp: boolean
    newLevel: number
    profile: GamificationProfile
}> {
    const backendUrl = getBackendUrl()

    const response = await fetch(
        `${backendUrl}/api/gamification/${tenantId}/profile/${userId}/xp`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ xpAmount, activityType, description, metadata }),
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to award XP: ${response.statusText}`)
    }

    const data = await response.json()
    return {
        ...data,
        profile: normalizeProfile(data.profile),
    }
}

/**
 * Update streak
 */
export async function updateStreak(
    tenantId: string,
    userId: number,
    date: string
): Promise<GamificationProfile> {
    const backendUrl = getBackendUrl()

    const response = await fetch(
        `${backendUrl}/api/gamification/${tenantId}/profile/${userId}/streak`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ date }),
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to update streak: ${response.statusText}`)
    }

    const data = await response.json()
    return normalizeProfile(data)
}

/**
 * Get leaderboard via proxy route (JWT authenticated)
 */
export async function getLeaderboard(
    _tenantId: string,
    timeframe: 'total' | 'monthly' | 'weekly' = 'monthly',
    limit: number = 10
): Promise<LeaderboardEntry[]> {
    const url = `/api/gamification/leaderboard?timeframe=${timeframe}&limit=${limit}`
    console.log('[Gamification API] getLeaderboard() → GET', url)

    try {
        const response = await fetch(url)
        console.log('[Gamification API] getLeaderboard() response:', response.status, response.statusText)

        if (!response.ok) {
            const errorBody = await response.text()
            console.error('[Gamification API] getLeaderboard() error body:', errorBody)
            throw new Error(`Failed to fetch leaderboard: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()
        console.log('[Gamification API] getLeaderboard() raw data:', data)
        return (data || []).map(normalizeLeaderboardEntry)
    } catch (err) {
        console.error('[Gamification API] getLeaderboard() exception:', err)
        throw err
    }
}

/**
 * Get activity logs via proxy route (JWT authenticated)
 */
export async function getActivityLogs(
    _tenantId: string,
    userId: number,
    limit: number = 50,
    _offset: number = 0
): Promise<{ logs: ActivityLog[]; total: number }> {
    const url = `/api/gamification/activity-logs?userId=${userId}&limit=${limit}&monthFilter=false`
    console.log('[Gamification API] getActivityLogs() → GET', url)

    try {
        const response = await fetch(url)
        console.log('[Gamification API] getActivityLogs() response:', response.status, response.statusText)

        if (!response.ok) {
            const errorBody = await response.text()
            console.error('[Gamification API] getActivityLogs() error body:', errorBody)
            throw new Error(`Failed to fetch activity logs: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()
        console.log('[Gamification API] getActivityLogs() raw data:', data)
        const logs = Array.isArray(data) ? data : (data.logs || [])
        const total = Array.isArray(data) ? data.length : (data.total || 0)
        return { logs, total }
    } catch (err) {
        console.error('[Gamification API] getActivityLogs() exception:', err)
        throw err
    }
}

/**
 * Update achievement progress
 */
export async function updateAchievement(
    tenantId: string,
    userId: number,
    achievementKey: string,
    progressIncrement?: number,
    progressValue?: number
): Promise<GamificationProfile> {
    const backendUrl = getBackendUrl()

    const response = await fetch(
        `${backendUrl}/api/gamification/${tenantId}/profile/${userId}/achievements/${achievementKey}`,
        {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ progressIncrement, progressValue }),
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to update achievement: ${response.statusText}`)
    }

    const data = await response.json()
    return normalizeProfile(data)
}

function getBackendUrl(): string {
    if (typeof window === 'undefined') {
        return process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'
    }

    const tenantContext = document.cookie
        .split('; ')
        .find(row => row.startsWith('tenant-context='))
        ?.split('=')[1]

    if (tenantContext) {
        try {
            const context = JSON.parse(decodeURIComponent(tenantContext))
            return context.backendUrl || process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'
        } catch (e) {
            console.error('Failed to parse tenant context:', e)
        }
    }

    return process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'
}
